import * as React from "react";
import { SearchBar } from "../../SearchBar";
import { useLocationConfiguration } from "../../LocationConfiguration";
import { useSyndeticsConfig } from "../../SyndeticsConfig";
import SearchBarContainer from "../../SearchBarContainer";
import {
  Link,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { Outlet } from "react-router-dom";
import {
  Box,
  Chip,
  Container,
  Divider,
  Skeleton,
  Tab,
  TabProps,
  Tabs,
} from "@mui/material";
import { useLanguage } from "../../Language";
import { axios } from "../../AxiosInstanceManager";
import {
  getAudienceFilter,
  getBranchFilters,
  getFacetFilters,
  SearchParametersDwr,
} from "./ls/LSSearch";
import { useNumberFormat } from "../../../hooks/useNumberFormat";
import { EbscoSearchResponse } from "./ebsco/EbscoSearchResponse";
import { BritannicaSearchResponse } from "./britannica/BritannicaSearchResponse";
import { GaleResponse } from "./gale/GaleSearchResponse";
import { WorldBookSearchResponse } from "./worldbook/WorldBookSearchResponse";
import { fetchWorldBookDbOptions } from "./worldbook/WorldBookSearchResults";
import { useBranchLimits } from "../../BranchLimits";
import { setSearchTermParam, useSearchSubmit } from "./useSearchSubmit";

export enum SearchDatabase {
  LS = "ls",
  EBSCO = "ebsco",
  GALE = "gale",
  BRITANNICA = "britannica",
  WORLDBOOK = "worldbook",
  NEWSBANK = "newsbank",
}

interface GetCountResponse {
  success: boolean;
  totalHits: number;
}

const keyFor = (db: SearchDatabase) => `searchState.v1:${db}`;

export const SearchStateStore = {
  get(db: SearchDatabase): string | null {
    try {
      return sessionStorage.getItem(keyFor(db));
    } catch {
      return null;
    }
  },
  set(db: SearchDatabase, qs: string) {
    try {
      sessionStorage.setItem(keyFor(db), qs || "");
    } catch {}
  },
  clear(db: SearchDatabase) {
    try {
      sessionStorage.removeItem(keyFor(db));
    } catch {}
  },
  clearAll() {
    Object.values(SearchDatabase).forEach((db) => {
      // guard against non-string enums if that changes
      if (typeof db === "string") this.clear(db as SearchDatabase);
    });
  },
};

export const SearchPage: React.FC = () => {
  const { locationConfiguration } = useLocationConfiguration();
  const { syndeticsConfig } = useSyndeticsConfig();
  const navigate = useNavigate();
  const { translate } = useLanguage();
  const { pathname } = useLocation();
  const searchSubmit = useSearchSubmit();
  const { configLimitedBranchIds, isConfigLimited } = useBranchLimits();
  const { pacSettings } = locationConfiguration || {};
  const { useEbsco, useBritannica, useGale, useNewsBank, useWorldBook } =
    pacSettings || {};

  const resultsContainerRef = React.useRef<HTMLDivElement>(null);
  const [params] = useSearchParams();
  const page = params.get("page");
  const prevPathnameRef = React.useRef<string>(pathname);

  function ScrollToTop() {
    resultsContainerRef.current?.scrollIntoView({
      behavior: "instant",
    });
  }

  const defaultSearchTerm = React.useMemo(() => {
    return params.get("term");
  }, [params]);

  React.useEffect(() => {
    if (prevPathnameRef.current === pathname) {
      const el = resultsContainerRef.current;
      ScrollToTop();
    }
    prevPathnameRef.current = pathname;
  }, [page, pathname]);

  const sources = React.useMemo(() => {
    const s = [
      {
        value: SearchDatabase.LS,
        label: translate("ls2pacDatabaseTabName"),
        getCount: async (params: URLSearchParams) => {
          try {
            const searchTerm = params.get("term");
            const facetFilters = getFacetFilters(params);
            const branchFilters = getBranchFilters(params);
            const targetAudience = getAudienceFilter(params);
            const payload: SearchParametersDwr = {
              searchTerm: searchTerm,
              hitsPerPage: 0,
              sortCriteria: "",
            };

            if (facetFilters) {
              payload.facetFilters = facetFilters;
            }

            if (branchFilters?.length) {
              payload.branchFilters = branchFilters;
            } else if (isConfigLimited) {
              payload.branchFilters = configLimitedBranchIds;
            }

            if (targetAudience) {
              payload.audienceCharacteristicsFilters = [targetAudience];
            }

            const { data } = await axios.post<GetCountResponse>(
              `/search/count`,
              payload
            );

            return data;
          } catch (e: unknown) {
            return {
              success: false,
              totalHits: 0,
            };
          }
        },
      },
    ];
    if (useEbsco) {
      s.push({
        value: SearchDatabase.EBSCO,
        label: translate("ebscoDatabaseTabName"),
        getCount: async (params: URLSearchParams) => {
          try {
            const searchTerm = params.get("term");
            const payload: SearchParametersDwr = {
              searchTerm,
              hitsPerPage: 0,
              sortCriteria: "",
            };
            const { data } = await axios.post<EbscoSearchResponse>(
              `/ebsco/search/count`,
              payload
            );
            return data;
          } catch (e: unknown) {
            return {
              success: false,
              totalHits: 0,
            };
          }
        },
      });
    }
    // if (useNewsBank) {
    //   s.push({
    //     value: SearchDatabase.NEWSBANK,
    //     label: translate("newsbankDatabaseTabName"),
    //     getCount: async (params: URLSearchParams) => {
    //       // TODO impl
    //       return {
    //         success: false,
    //         totalHits: 0,
    //       };
    //     },
    //   });
    // }
    if (useBritannica) {
      s.push({
        value: SearchDatabase.BRITANNICA,
        label: translate("britannicaDatabaseTabName"),
        getCount: async (params: URLSearchParams) => {
          try {
            const searchTerm = params.get("term");
            const payload: SearchParametersDwr = {
              searchTerm,
              hitsPerPage: 0,
              sortCriteria: "",
            };
            const { data } = await axios.post<BritannicaSearchResponse>(
              `/britannica/search/count`,
              payload
            );
            return data;
          } catch (e: unknown) {
            return {
              success: false,
              totalHits: 0,
            };
          }
        },
      });
    }
    if (useGale) {
      s.push({
        value: SearchDatabase.GALE,
        label: translate("galeDatabaseTabName"),
        getCount: async (params: URLSearchParams) => {
          try {
            const searchTerm = params.get("term");
            const payload: SearchParametersDwr = {
              searchTerm,
              hitsPerPage: 0,
              sortCriteria: "",
            };
            const { data } = await axios.post<GaleResponse>(
              `/gale/search/count`,
              payload
            );
            return data;
          } catch (e: unknown) {
            return {
              success: false,
              totalHits: 0,
            };
          }
        },
      });
    }
    if (useWorldBook) {
      s.push({
        value: SearchDatabase.WORLDBOOK,
        label: translate("worldbookDatabaseTabName"),
        getCount: async (params: URLSearchParams) => {
          try {
            const { defaultDb } = await fetchWorldBookDbOptions();
            const searchTerm = params.get("term");
            const selectedDb = params.get("db");
            const payload: SearchParametersDwr = {
              searchTerm,
              hitsPerPage: 0,
              sortCriteria: "",
              worldBookDatabaseList: selectedDb
                ? [selectedDb]
                : [defaultDb?.value],
            };
            const { data } = await axios.post<WorldBookSearchResponse>(
              `/worldbook/search/count`,
              payload
            );
            return data;
          } catch (e: unknown) {
            return {
              success: false,
              totalHits: 0,
            };
          }
        },
      });
    }
    return s;
  }, [useEbsco, useBritannica, useGale, useNewsBank, useWorldBook, translate]);

  const active = React.useMemo(() => {
    return sources.find((k) => pathname.includes(`/search/${k.value}`))?.value;
  }, [sources, pathname]);

  React.useEffect(() => {
    return () => {
      // Clear tab search state when unmounting /search route tree
      // this does not fire in the middle of an SSO or Google Sign In redirect or during a hard refresh
      // so tab state is preserved while logging no matter the method
      SearchStateStore.clearAll();
    };
  }, []);

  return (
    <>
      <SearchBarContainer>
        <SearchBar
          autoFocus={true}
          sx={{ m: "0 auto" }}
          limitToKids={false}
          voiceSearchEnabled={
            locationConfiguration?.pacSettings?.displaySettings?.voiceInterface
          }
          onClickSearch={searchSubmit}
          onClickTitle={(resource) => {
            navigate(`/resource/${resource.id}`);
          }}
          syndeticsClientCode={syndeticsConfig?.clientCode}
          branchFilters={locationConfiguration?.filterBranches}
          defaultSearchTerm={defaultSearchTerm}
        ></SearchBar>
      </SearchBarContainer>
      <Container ref={resultsContainerRef}>
        {active && sources.length > 1 && (
          <>
            <Tabs
              value={active}
              scrollButtons="auto"
              variant="scrollable"
              allowScrollButtonsMobile
              textColor="secondary"
              indicatorColor="secondary"
            >
              {sources.map(({ label, value, getCount }) => (
                <DatabaseTab
                  key={value}
                  value={value}
                  label={label}
                  active={value === active}
                  getCount={getCount}
                />
              ))}
            </Tabs>
            <Divider />
          </>
        )}
        <Outlet />
      </Container>
      {/* </div> */}
    </>
  );
};

interface DatabaseTabProps extends TabProps {
  value: SearchDatabase;
  label: string;
  active: boolean;
  getCount: (params: URLSearchParams) => Promise<GetCountResponse>;
}

export const DatabaseTab = ({
  value,
  label,
  active,
  getCount,
  ...props
}: DatabaseTabProps) => {
  const [params] = useSearchParams();
  const searchTerm = React.useMemo(() => {
    return params.get("term");
  }, [params]);
  const [count, setCount] = React.useState<number>();
  const [loadingCount, setLoadingCount] = React.useState<boolean>(false);
  const nf = useNumberFormat();

  // check session storage if not the active tab
  // if empty, fallback to term=${searchTerm}

  const defaultSearchState = React.useMemo(() => {
    if (active) {
      return params.toString();
    }
    const storedValue = SearchStateStore.get(value);
    if (storedValue) {
      return storedValue;
    }
    const tabParams = new URLSearchParams();
    tabParams.set("term", searchTerm);
    return tabParams.toString();
  }, [active, params, searchTerm, value]);

  const [search, setSearch] = React.useState(defaultSearchState);

  // TODO if tabs no longer rendered, don't update state

  // if we are active and our params are changing, update the state
  React.useEffect(() => {
    if (active) {
      setSearch(params.toString());
      SearchStateStore.set(value, params.toString());
    } else {
      const prevParams = new URLSearchParams(search);
      const currentSearchTerm = params.get("term");
      if (prevParams.get("term") !== currentSearchTerm) {
        const searchString = setSearchTermParam(
          prevParams,
          currentSearchTerm
        ).toString();
        setSearch(searchString); // preserve filters, etc. but update the search string
        SearchStateStore.set(value, searchString);
      }
    }
  }, [active, params, value]);

  // if the search has changed
  React.useEffect(() => {
    setLoadingCount(true);
    getCount(new URLSearchParams(search))
      .then(({ totalHits, success }) => {
        if (success) {
          setCount(totalHits);
        } else {
          setCount(null);
        }
      })
      .catch(() => {
        setCount(null);
      })
      .finally(() => {
        setLoadingCount(false);
      });

    return () => {};
  }, [search]);

  return (
    <Tab
      component={Link}
      to={{
        pathname: `/search/${value}`,
        search: search,
      }}
      value={value}
      label={
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flexDirection: "row",
            gap: 1,
          }}
        >
          <span>{label}</span>
          {!loadingCount && (
            <Chip
              size="small"
              variant={active ? "filled" : "outlined"}
              color={active ? "secondary" : "default"}
              label={Number.isFinite(count) ? nf.format(count) : "?"}
            />
          )}
          {loadingCount && (
            <Skeleton variant="circular">
              <Chip size="small" label={0} />
            </Skeleton>
          )}
        </Box>
      }
      {...props}
    ></Tab>
  );
};
